import TranslateIcon from "@mui/icons-material/Translate";
import CloseIcon from "@mui/icons-material/Close";
import { ClickAwayListener, IconButton, ListItem } from "@mui/material";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import uniqid from "uniqid";
import styled from "styled-components"; // Import styled-components
import { StyledLanguageWrapper } from "../../Styles/StyledComponents";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { ReactComponent as SelectedLanguageIcon } from "../../../assets/images/selectedIcon.svg";
import {
  allMessagesStore,
  setBotLanguage,
  removeAllMessages,
  setIsSessionIdFromLocalStorage,
  setSessionId,
  removeAllMessagesForLanguage,
  setLanguageSelectionPopup,
  getDisableHeaderSelection,
  setDisableHeaderSelection,
  setLanguageSelector,
  getLanguageSelector,
} from "../../../Store/Slices/socket/AllMessagesSlice";
import { botInfoStore } from "../../../Store/Slices/socket/BotInfoSlice";
import { titlize } from "../../utils/utils";
import AppDrawer from "../UtilsComponents/AppDrawer";
import { PreviewType } from "../../utils/Enum";
import { HostWindowCommService } from "../../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../../Services/IframeHostService/models";
import useMessagesData from "../../hooks/useMessagesData";

interface Props {
  setShowStartScreen?: (value: boolean) => void;
  onFormSubmitted?: () => void;
}

const StyledListItem = styled(ListItem)<{ isSelected: boolean }>`
  padding: 16px 20px;
  cursor: pointer;
  color: ${props => props.color || "#121ecf"};
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props =>
    props.isSelected ? "rgba(18, 30, 207, 0.1)" : "transparent"};
  transform: none;

  &:hover {
    background-color: rgba(18, 30, 207, 0.05);
    transform: none;
  }
`;

const AppLanguageSelector: React.FC<Props> = ({ setShowStartScreen, onFormSubmitted }) => {
  const dispatch = useAppDispatch();
  const { botConfig } = useSelector(useBotConfigStore);
  const { widgetPreviewType, chatOpened } = useSelector(botInfoStore);
  const { botLanguage, typingStatus, languageSelectionPopup } = useSelector(allMessagesStore);
  const { sessionId, allMessages } = useAppSelector(allMessagesStore);
  const [langauageData, setLanguagesData] = useState<any>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [showLanguageSwitchOption, setShowLanguageSwitchOption] = useState<boolean>(true);
  const languageSelector = useAppSelector(getLanguageSelector);
  let commService = React.useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const multiLingual = true;
  const displayLanguageOption = true;

  useEffect(() => {
    if (multiLingual && displayLanguageOption) {
      setShowLanguageSwitchOption(true);
    } else {
      setShowLanguageSwitchOption(false);
    }

    if (botConfig?.chatbot?.chatbot_languages?.length > 0) {
      const filterActiveLanguages = botConfig?.chatbot?.chatbot_languages?.filter(
        (language: any) => language?.isActive
      );
      setLanguagesData(filterActiveLanguages);
    } else {
      dispatch(setBotLanguage("english"));
    }
  }, [botConfig, dispatch]); // Empty dependency array since chatbotLanguages is a constant

  const handleLanguageClick = (item: any) => {
    dispatch(setBotLanguage(item?.value || "english"));
    commService.postMessage({
      event_type: OEvents.CHATBOT_SESSION_ID,
      data: null,
    });
    dispatch(removeAllMessagesForLanguage());
    dispatch(setDisableHeaderSelection(true));
    setShowStartScreen?.(false);
    onFormSubmitted?.();
    dispatch(setLanguageSelector(false));
    dispatch(setLanguageSelectionPopup(false));
  };

  let DivBoxref = useRef<any>(null);
  useEffect(() => {
    if (chatOpened && DivBoxref && DivBoxref?.current) {
      setTimeout(() => {
        DivBoxref?.current?.classList.add("active");
      }, 800);
      setTimeout(() => {
        DivBoxref?.current?.classList.remove("active");
      }, 2300);
    }
  }, [botLanguage, chatOpened]);

  if ((!showLanguageSwitchOption && !chatOpened)) {
    return null;
  }
  if(langauageData.length<=0){
    return null;
  }
  return (
    <StyledLanguageWrapper
      style={{
        display: showLanguageSwitchOption ? "flex" : "none",
        pointerEvents:
          widgetPreviewType !== PreviewType.getaWidgetPreview && !typingStatus
            ? "auto"
            : "none",
      }}
    >
       {
        !languageSelector && <div
        onClick={() => dispatch(setLanguageSelectionPopup(true))}
        ref={DivBoxref}
        className="LanguageIconBox"
      >
        <IconButton
          onClick={() => dispatch(setLanguageSelectionPopup(true))}
          className="languageIcon"
          size="small"
        >
          <TranslateIcon />
        </IconButton>
        <p className="currentLanguage">
          {botLanguage ? titlize(botLanguage) : "English"}
        </p>
      </div>
       }

      <AppDrawer
        showDrawer={languageSelectionPopup}
        setShowDrawer={() => dispatch(setLanguageSelectionPopup(false))}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          maxHeight: "80vh",
          overflow: "hidden",
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
          zIndex: 9999,
        }}
        intialX={0}
        intialY={"100%"}
        animateX={0}
        animateY={0}
        exitX={0}
        exitY={"100%"}
        animateHeight="auto"
      >
        <ClickAwayListener onClickAway={() => dispatch(setLanguageSelectionPopup(false))}>
          <div
            className="languageSelectListWrapper"
            style={{
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              fontFamily: "Inter",
              fontSize: "14px",
            }}
          >
            {/* Header Section */}
            <div
              style={{
                padding: "10px 20px",
                borderBottom: "1px solid rgba(18, 30, 207, 0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: botConfig?.theme?.button?.text,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "#000",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                Select Language
              </h3>
              <IconButton
                onClick={() => dispatch(setLanguageSelectionPopup(false))}
                style={{
                  padding: "8px",
                  color: "#121ecf",
                  backgroundColor: "rgba(18, 30, 207, 0.05)",
                  borderRadius: "8px",
                }}
              >
                <CloseIcon style={{ fontSize: "18px" }} />
              </IconButton>
            </div>

            {/* Language List */}
            <div>
              {[{ label: "English", isActive: true, name:"English", value: "english" }, ...(langauageData || [])]?.map((curLang: any, index: number) => (
                <StyledListItem
                  key={curLang.value}
                  onClick={() => handleLanguageClick(curLang)}
                  color={botConfig?.theme?.button?.background}
                  isSelected={botLanguage === curLang.value}
                >
                  <span style={{color: botConfig?.theme?.button?.background}}>{titlize(curLang.label)}</span>
                  {botLanguage === curLang.value && (
                    <SelectedLanguageIcon
                      style={{
                        color: botConfig?.theme?.button?.background || "#121ecf",
                        width: "18px",
                        height: "18px",
                      }}
                      aria-label="Selected language checkmark"
                    />
                  )}
                </StyledListItem>
              ))}
            </div>

          </div>
        </ClickAwayListener>
      </AppDrawer>
    </StyledLanguageWrapper>
  );
};

export default memo(AppLanguageSelector);